import React, {useEffect, useRef, useState} from "react";
import "./SearchInput.scss";
import Strings from "../../config/Strings";
import {Form} from "react-bootstrap";
import {FaSearch} from "react-icons/fa";
import {GetSearchSuggestions} from "../../resources/search/SearchService";

//suggestions are requested as soon as this many characters are entered and the user paused typing for a moment
const SUGGESTION_MIN_LENGTH = 2;
const SUGGESTION_DELAY_MILLIS = 250;

const CLASSIFICATION_LABEL_KEYS = {
    RADIOPLAY: 'SEARCH_SUGGESTION_RADIOPLAY',
    AUDIOBOOK: 'SEARCH_SUGGESTION_AUDIOBOOK',
    MUSIC: 'SEARCH_SUGGESTION_MUSIC',
    PODCAST: 'SEARCH_SUGGESTION_PODCAST',
    PLAYLIST: 'SEARCH_SUGGESTION_PLAYLIST',
};

function escapeRegExp(text) {
    return text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

//highlights the beginnings of words that match one of the entered words, e.g. "Paw" in "Der Paw-Patrol-Rettungskurs"
function highlightMatches(text, query) {
    const words = query.trim().split(/\s+/).filter(word => word).map(escapeRegExp);
    if (words.length === 0) {
        return text;
    }
    const regex = new RegExp("(^|[^\\p{L}\\p{N}])(" + words.join("|") + ")", "giu");
    const parts = [];
    let lastIndex = 0;
    let match;
    while ((match = regex.exec(text)) !== null) {
        const start = match.index + match[1].length;
        parts.push(text.substring(lastIndex, start));
        parts.push(<mark key={start}>{match[2]}</mark>);
        lastIndex = start + match[2].length;
    }
    parts.push(text.substring(lastIndex));
    return parts;
}

//e.g. "Serie · ab 3 Jahren · 471 Folgen" or "PAW Patrol · Hörspiel · ab 3 J."
function getSuggestionDetails(suggestion) {
    const details = [];
    if (suggestion.type === 'SERIES') {
        details.push(Strings.SEARCH_SUGGESTION_SERIES);
        if (suggestion.ageMin != null) {
            details.push(Strings.SEARCH_SUGGESTION_SERIES_AGE.replace("{0}", suggestion.ageMin));
        }
        if (suggestion.episodeCount) {
            details.push(suggestion.episodeCount === 1
                    ? Strings.SEARCH_SUGGESTION_ONE_EPISODE
                    : Strings.SEARCH_SUGGESTION_EPISODES.replace("{0}", suggestion.episodeCount));
        }
    } else {
        if (suggestion.subtitle) {
            details.push(suggestion.subtitle);
        }
        if (CLASSIFICATION_LABEL_KEYS[suggestion.classification]) {
            details.push(Strings[CLASSIFICATION_LABEL_KEYS[suggestion.classification]]);
        }
        if (suggestion.ageMin != null) {
            details.push(Strings.SEARCH_SUGGESTION_PRODUCT_AGE.replace("{0}", suggestion.ageMin));
        }
    }
    return details.join(" · ");
}

function getSuggestionUrl(suggestion) {
    if (suggestion.type === 'SERIES') {
        return "/series/" + suggestion.slug + "/" + suggestion.id;
    }
    return suggestion.slug ? "/product/" + suggestion.slug + "/" + suggestion.id : "/product/" + suggestion.id;
}

/**
 * Search field with search button and suggestions of series and products below the field while typing.
 * <p>
 * Without onSearch the search page is opened with the entered query (needs the router history). The search page
 * itself passes query/onQueryChange to keep the query in its filter and onSearch to start the search, and
 * showDescription to show the headline and description above the search field. languages restricts the suggestions
 * to these languages (ISO3 codes), a selected suggestion opens the page of the series or product.
 */
export const SearchInput = ({query, onQueryChange, onSearch, history, showDescription = false, languages, ageMin, ageMax}) => {
    const [ownQuery, setOwnQuery] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [activeSuggestion, setActiveSuggestion] = useState(-1);
    //suggestions are only requested for text the user typed, not for a query set by the page
    const [typedText, setTypedText] = useState(null);
    const latestRequest = useRef(0);
    const controlled = query !== undefined;
    const value = controlled ? query : ownQuery;
    const languageKey = languages ? languages.join(",") : "";

    useEffect(() => {
        if (typedText == null || typedText.trim().length < SUGGESTION_MIN_LENGTH) {
            setSuggestions([]);
            return undefined;
        }
        const timeout = setTimeout(async () => {
            //an answer to an older request must not overwrite the suggestions of the current text
            const request = ++latestRequest.current;
            const response = await GetSearchSuggestions(typedText.trim(), languageKey ? languageKey.split(",") : [], ageMin, ageMax);
            if (request === latestRequest.current) {
                setSuggestions(response.error ? [] : (response.result?.suggestions || []));
                setActiveSuggestion(-1);
            }
        }, SUGGESTION_DELAY_MILLIS);
        return () => clearTimeout(timeout);
    }, [typedText, languageKey, ageMin, ageMax]);

    const closeSuggestions = () => {
        latestRequest.current++;
        setShowSuggestions(false);
        setActiveSuggestion(-1);
    };

    const handleChange = (e) => {
        if (controlled) {
            onQueryChange(e.target.value);
        } else {
            setOwnQuery(e.target.value);
        }
        setTypedText(e.target.value);
        setShowSuggestions(true);
    };

    const handleSubmit = (e) => {
        //without this, the browser performs its own (unnamed-input, so query-string-stripping)
        //form submission, reloading the page and discarding all state
        e.preventDefault();
        if (activeSuggestion === suggestions.length && showSuggestions) {
            search();
            return;
        }
        if (activeSuggestion >= 0 && showSuggestions && suggestions[activeSuggestion]) {
            openSuggestion(suggestions[activeSuggestion]);
            return;
        }
        search();
    };

    const search = () => {
        closeSuggestions();
        if (onSearch) {
            onSearch(value);
        } else {
            history.push('/search/?query=' + encodeURIComponent(value.trim()));
        }
    };

    //a full page load like the links of the search results, the product and series pages don't reload
    //their content when only the id in the route changes
    const openSuggestion = (suggestion) => {
        closeSuggestions();
        window.location.href = getSuggestionUrl(suggestion);
    };

    const handleKeyDown = (e) => {
        if (!showSuggestions || suggestions.length === 0) {
            return;
        }
        //the last entry is the link to all results
        const entries = suggestions.length + 1;
        if (e.key === "ArrowDown") {
            e.preventDefault();
            setActiveSuggestion((activeSuggestion + 1) % entries);
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveSuggestion(activeSuggestion <= 0 ? entries - 1 : activeSuggestion - 1);
        } else if (e.key === "Escape") {
            closeSuggestions();
        }
    };

    const suggestionsVisible = showSuggestions && suggestions.length > 0;

    const renderSuggestion = (suggestion, index) => (
        <div key={suggestion.type + suggestion.id} id={"search-suggestion-" + index}
             role="option" aria-selected={index === activeSuggestion}
             className={"search-suggestion" + (index === activeSuggestion ? " active" : "")}
             //mousedown instead of click, the click would come after the blur closed the list
             onMouseDown={(e) => {
                 e.preventDefault();
                 openSuggestion(suggestion);
             }}>
            {suggestion.imageUrl
                ? <img className="search-suggestion-image" src={suggestion.imageUrl} alt=""/>
                : <span className="search-suggestion-image"/>}
            <span className="search-suggestion-content">
                <span className="search-suggestion-text">{highlightMatches(suggestion.text, value)}</span>
                <span className="search-suggestion-details">{getSuggestionDetails(suggestion)}</span>
            </span>
        </div>
    );

    const renderSuggestions = () => {
        const series = suggestions.filter(suggestion => suggestion.type === 'SERIES');
        const products = suggestions.filter(suggestion => suggestion.type !== 'SERIES');
        const allResultsIndex = suggestions.length;
        return (
            <div id="search-suggestions" className="search-suggestions" role="listbox">
                {series.length > 0 &&
                    <div className="search-suggestion-group" role="presentation">{Strings.SEARCH_SUGGESTION_GROUP_SERIES}</div>}
                {series.map((suggestion, index) => renderSuggestion(suggestion, index))}
                {products.length > 0 &&
                    <div className="search-suggestion-group" role="presentation">{Strings.SEARCH_SUGGESTION_GROUP_TITLES}</div>}
                {products.map((suggestion, index) => renderSuggestion(suggestion, series.length + index))}
                <div id={"search-suggestion-" + allResultsIndex} role="option" aria-selected={activeSuggestion === allResultsIndex}
                     className={"search-suggestion-all-results" + (activeSuggestion === allResultsIndex ? " active" : "")}
                     onMouseDown={(e) => {
                         e.preventDefault();
                         search();
                     }}>
                    {Strings.SEARCH_SUGGESTION_ALL_RESULTS.replace("{0}", value.trim())}
                </div>
            </div>
        );
    };

    return (
        <>
            <Form onSubmit={handleSubmit}>
                {showDescription &&
                    <div className="search-query-description-container">
                        <h1>Wonach suchst Du?</h1>
                        <p>Titel, Figur oder Thema eingeben – mit der erweiterten Suche nach Alter, Art und Dauer eingrenzen.</p>
                    </div>
                }
                <div className="search-query-input-container">
                    <div className="search-query-input-wrapper">
                        <Form.Control type="search" value={value}
                                      className="search-query-input"
                                      placeholder={Strings.SEARCH_INPUT_PLACEHOLDER}
                                      autoComplete="off"
                                      role="combobox"
                                      aria-autocomplete="list"
                                      aria-expanded={suggestionsVisible}
                                      aria-controls="search-suggestions"
                                      aria-activedescendant={activeSuggestion >= 0 ? "search-suggestion-" + activeSuggestion : undefined}
                                      onChange={handleChange}
                                      onKeyDown={handleKeyDown}
                                      onBlur={closeSuggestions}/>
                        {suggestionsVisible && renderSuggestions()}
                    </div>
                    <button id={"search-button"} type="submit" aria-label={Strings.SEARCH_BUTTON_TEXT}><FaSearch/></button>
                </div>
            </Form>
        </>
    );
};
